<template>
  <div class="rates-table">

    <xolas-table
      :title="titleCase(view) + ' Rates'"
      :url="'/rates/' + view"
      :table-ref="view + 'ratesttable'"
      :fields="fields"
      :hide-top="hideTop"
      :show-search="showSearch"
      :show-refresh="showRefresh"
      :search-placeholder="searchPlaceholder">

      <!-- Add-action cluster (hidden when noAdd) -->
      <template v-if="!noAdd" #actions>
        <sharer-rates-actions
          v-if="isSharer()"
          :view="view"
          :show-range="!isUtil()"
          :table-ref="view + 'ratesttable'" />
        <rates-action
          v-else
          v-bind="addPropsForView" />
      </template>

      <!-- Sharer-only: read-only inline-tier cell -->
      <template #tiers="props">
        <rate-tiers-cell :row-data="props.rowData" />
      </template>

      <!-- Per-row actions: edit / duplicate / manage-versions / delete stacked vertically -->
      <template #rowactions="props">
        <div class="rate-row-actions">
          <rates-action
            v-if="isManager('finance|management|admin')"
            :view="view"
            :show-range="!isUtil()"
            :row-data="props.rowData"
            :allow-publish="true" />
          <rates-action
            v-if="isManager('finance|management|admin')"
            :view="view"
            :show-range="!isUtil()"
            :row-data="props.rowData"
            :duplicate="true"
            @saved="onDuplicateSaved" />
          <el-button
            v-if="isSharer() && isManager('finance|management|admin')"
            circle
            size="small"
            icon="fa fa-code-branch"
            aria-label="Manage versions"
            title="Manage versions"
            @click="openVersionManager(props.rowData)" />
          <el-tooltip
            v-if="isManager('finance')"
            placement="left"
            :disabled="!(Number(props.rowData.usage || 0) > 0)"
            :content="'In use by ' + (props.rowData.usage || 0) + ' site(s)/sharer(s) — can\'t delete.'">
            <span>
              <el-button
                type="danger"
                icon="fa fa-trash"
                circle
                size="small"
                aria-label="Delete rate"
                title="Delete rate"
                :disabled="Number(props.rowData.usage || 0) > 0"
                @click="deleteRate(props.rowData)" />
            </span>
          </el-tooltip>
        </div>
      </template>

    </xolas-table>

    <!-- Single table-level manage-versions drawer -->
    <el-drawer
      :title="'Manage versions — ' + (activeRate ? activeRate.name : '')"
      :visible.sync="versionDrawerVisible"
      direction="btt"
      size="96.3%"
      append-to-body
      @opened="onVersionDrawerOpened">
      <rate-version-manager
        v-if="activeRate"
        ref="versionManager"
        :row-data="activeRate"
        :table-ref="view + 'ratesttable'"
        @changed="onVersionChanged" />
    </el-drawer>

  </div>
</template>


<script>

import NumberMixins from '@/mixins/NumberMixins'
import FormattingMixins from '@/mixins/FormattingMixins'
import PostMixins from '@/mixins/PostMixins'

import RatesAction from './actions/RatesAction'
import SharerRatesActions from './actions/tiers/SharerRatesActions'
import RateTiersCell from './RateTiersCell'
import RateVersionManager from './RateVersionManager'

export default {
  mixins: [FormattingMixins, NumberMixins, PostMixins],

  components: {
    RatesAction,
    SharerRatesActions,
    RateTiersCell,
    RateVersionManager
  },

  props: {
    view: {
      type: String,
      validator: function (value) {
        return value !== null ? ['sharer', 'tenancy', 'utility'].indexOf(value) !== -1 : true
      },
      default: 'sharer'
    },

    noAdd: {
      type: Boolean,
      default: false
    },

    siteId: {
      type: Number,
      default: null
    },

    owner: {
      type: Number,
      default: null
    },

    hideTop: {
      type: Boolean,
      default: false
    },

    showSearch: {
      type: Boolean,
      default: true
    },

    showRefresh: {
      type: Boolean,
      default: true
    },

    searchPlaceholder: {
      type: String,
      default: 'Name...'
    }
  },

  methods: {
    onDuplicateSaved: function (newRate) {
      this.$emit('saved', newRate)
      // Table refresh is already handled by saveForm via the tableRef arg
      // passed through RatesAction. No extra refresh needed here.
    },

    deleteRate: function (row) {
      if (row.id) {
        this.deleteItem(this, row.id, '/rates', 'Delete rate ' + row.name + ' [' + row.status + ']', null, true, this.view + 'ratesttable')
      } else {
        this.$message({ type: 'warning', message: "No ID, can't delete." })
      }
    },

    openVersionManager: function (row) {
      this.activeRate = row
      this.versionDrawerVisible = true
    },

    onVersionDrawerOpened: function () {
      if (this.$refs.versionManager) {
        this.$refs.versionManager.open()
      }
    },

    onVersionChanged: function () {
      // Table already refreshes via eventTableRef fired by send() with tableRef.
    },

    isTenancy: function () {
      return (this.view === 'tenancy')
    },

    isUtil: function () {
      return (this.view === 'utility')
    },

    isSharer: function () {
      return (this.view === 'sharer')
    }
  },

  computed: {
    // Props for the non-sharer add-action (tenancy / utility)
    addPropsForView: function () {
      return {
        rowData: null,
        editMode: false,
        view: this.view,
        tableRef: this.view + 'ratesttable',
        showRange: (this.view === 'utility' ? false : true)
      }
    }
  },

  data: function () {
    return {
      telcoId: this.isTelco() ? Number(this.org().id) : this.owner,
      versionDrawerVisible: false,
      activeRate: null,

      fields: [
        {
          name: '__sequence',
          title: '#',
          sortField: 'id'
        },
        {
          name: 'groupId',
          title: 'group ID',
          sortField: 'groupId',
          titleClass: 'hidden-md-and-down',
          dataClass: 'hidden-md-and-down'
        },
        {
          name: 'status',
          title: 'Status',
          sortField: 'status',
          titleClass: 'hidden-md-and-down',
          dataClass: 'hidden-md-and-down'
        },
        {
          name: 'height',
          title: 'Height',
          callback: this.formatInteger,
          titleClass: 'hidden-sm-and-down',
          dataClass: 'hidden-sm-and-down',
          visible: !this.isUtil() && !this.isTenancy()
        },
        {
          name: 'users',
          title: 'Users',
          callback: this.formatInteger,
          titleClass: 'hidden-sm-and-down',
          dataClass: 'hidden-sm-and-down',
          visible: this.isUtil()
        },
        {
          name: 'cabin',
          title: 'Cabin',
          callback: this.formatInteger,
          titleClass: 'hidden-sm-and-down',
          dataClass: 'hidden-sm-and-down',
          visible: this.isUtil()
        },
        {
          name: 'name',
          title: 'Name',
          sortField: 'name',
          minWidth: 170,
          visible: !this.isUtil()
        },
        {
          name: 'percentIncreasePerYear',
          title: 'Incr. Per Year',
          callback: this.formatPercent,
          titleClass: 'hidden-sm-and-down',
          dataClass: 'hidden-sm-and-down',
          visible: this.isTenancy()
        },
        {
          name: 'fromMonth',
          title: 'Starting Month',
          callback: this.formatInteger,
          titleClass: 'hidden-sm-and-down',
          dataClass: 'hidden-sm-and-down',
          visible: this.isTenancy()
        },
        {
          name: 'toMonth',
          title: 'End Month',
          callback: this.formatInteger,
          titleClass: 'hidden-sm-and-down',
          dataClass: 'hidden-sm-and-down',
          visible: this.isTenancy()
        },
        // Sharer-only: read-only inline-tier cell (replaces flat telco1..10 cols)
        // minWidth gives the 6-rate grid + tenor room (el-table otherwise
        // auto-sizes it far too narrow and the values overflow).
        {
          name: '__slot:tiers',
          title: 'Tiers & rates',
          minWidth: 420,
          visible: this.isSharer()
        },
        // Utility-only: flat Rate #1..#10 columns
        // (was !isTenancy() — now isUtil() so sharer uses the tiers cell above)
        {
          name: 'telco1',
          title: 'Rate #1',
          callback: this.formatNumber,
          visible: this.isUtil()
        },
        {
          name: 'telco2',
          title: 'Rate #2',
          callback: this.formatNumber,
          visible: this.isUtil()
        },
        {
          name: 'telco3',
          title: 'Rate #3',
          callback: this.formatNumber,
          visible: this.isUtil()
        },
        {
          name: 'telco4',
          title: 'Rate #4',
          callback: this.formatNumber,
          visible: this.isUtil()
        },
        {
          name: 'telco5',
          title: 'Rate #5',
          callback: this.formatNumber,
          visible: this.isUtil()
        },
        {
          name: 'telco6',
          title: 'Rate #6',
          callback: this.formatNumber,
          visible: this.isUtil()
        },
        {
          name: 'telco7',
          title: 'Rate #7',
          callback: this.formatNumber,
          visible: this.isUtil()
        },
        {
          name: 'telco8',
          title: 'Rate #8',
          callback: this.formatNumber,
          visible: this.isUtil()
        },
        {
          name: 'telco9',
          title: 'Rate #9',
          callback: this.formatNumber,
          visible: this.isUtil()
        },
        {
          name: 'telco10',
          title: 'Rate #10',
          callback: this.formatNumber,
          visible: this.isUtil()
        },
        {
          // Edit / duplicate / delete stacked vertically in one column.
          name: '__slot:rowactions',
          width: 84,
          align: 'center'
        }
      ]
    }
  },

  mounted: function () {
    if (!this.telcoId && this.isTelco()) {
      this.telcoId = Number(this.org().id)
    }
  }
}

</script>

<style scoped>
/* Tighten the gap between the filter-bar action buttons (incl. the Default
   tier template button) and the rule above the table. Scoped to the rate
   tables only — the shared <hr> otherwise carries a ~20px top margin. */
.rates-table ::v-deep hr {
  margin-top: 8px;
}

/* Stack the per-row edit / duplicate / delete buttons vertically. */
.rate-row-actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}
</style>
